"use client";

import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<"table"> & { containerClassName?: string }) {
  return (
    <div
      data-slot="table-container"
      className={cn("relative w-full overflow-x-auto", containerClassName)}
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead data-slot="table-header" className={cn("[&_tr]:border-b", className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b transition-colors hover:bg-muted/50 has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "font-condensed font-medium text-[var(--fg-3)] h-10 px-2 text-left align-middle text-[13px] tracking-[0.06em] whitespace-nowrap uppercase [&_button]:uppercase [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Заголовок сортируемой колонки. Presentation-only: рисует то направление, что дали,
 * и зовёт `onToggle` — цикл asc→desc→none принадлежит владельцу состояния (TanStack или странице).
 *
 * `direction: false` и `onToggle: (event: unknown) => void` — типы `column.getIsSorted()` и
 * `column.getToggleSortingHandler()`: TanStack потребляет примитив напрямую, без адаптера.
 */
function SortableTableHead({
  className,
  children,
  sortable = true,
  direction,
  onToggle,
  align = "left",
  "data-testid": testId,
  ...props
}: React.ComponentProps<"th"> & {
  sortable?: boolean;
  direction?: "asc" | "desc" | false | null;
  onToggle?: (event: unknown) => void;
  align?: "left" | "right";
  "data-testid"?: string;
}) {
  const dir = direction || null;
  // Кнопка — inline-flex, т.е. inline-level: правое выравнивание даёт text-align ячейки, не ml-auto.
  const headClassName = cn(align === "right" && "text-right", className);

  if (!sortable) {
    return (
      <TableHead className={headClassName} data-testid={testId} {...props}>
        {children}
      </TableHead>
    );
  }

  return (
    <TableHead
      className={headClassName}
      aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
      {...props}
    >
      {/* Типографику лейбла не переопределяем — она приходит из TableHead ([&_button]:uppercase). */}
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium"
        onClick={onToggle}
        data-testid={testId}
      >
        {children}
        {dir === "asc" ? (
          <ArrowUp className="h-3 w-3" />
        ) : dir === "desc" ? (
          <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn("p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0", className)}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  SortableTableHead,
  TableRow,
  TableCell,
  TableCaption,
};
